import { createEntityAdapter, createSlice, EntityId } from '@reduxjs/toolkit';
import { RootState } from "../index";
import { fetchAsset, fetchAssets, createAsset, updateAsset, reorderAssets } from "src/actions/mine_craft/assetsActions";

export interface AssetInterface {
  url: string;

  id: number;
  app_id: number;

  image_url: string;
  type_id: number;

  created_at: string;
  updated_at: string;
}

export interface initialInterface {
  error: string | null;
  status: 'idle' | 'loading' | 'succeeded' | 'failed';
  count: number;

  editId: EntityId | null;
  editError: string | null

  newParams: AssetInterface | undefined;
  newError: string | null;

  newGroupParams: AssetInterface | undefined;
  newGroupError: string | null;

  reorderIds: number[] | null;
}

const initialState: initialInterface = {
  error: null,
  status: 'idle',
  count: 0,

  editId: null,
  editError: null,

  newParams: undefined,
  newError: null,

  newGroupParams: undefined,
  newGroupError: null,

  reorderIds: null,
};

const adaptor = createEntityAdapter<AssetInterface>();

const slice = createSlice({
  name: 'assets',
  initialState: adaptor.getInitialState(initialState),
  reducers: {
    setStatus: (state, action) => {
      state.status = action.payload;
    },
    setNewParams: (state, action) => {
      const defaults = {}

      state.newParams = action.payload ? { ...defaults, ...action.payload } : undefined;
      state.newError  = initialState.newError;
    },
    setNewGroupParams: (state, action) => {
      state.newGroupParams = action.payload;
      state.newGroupError  = initialState.newGroupError;
    },
    setNewError: (state, action) => {
      state.newError = action.payload;
    },
    setEditId: (state, action) => {
      state.editId    = action.payload;
      state.editError = initialState.editError;
    },
    setEditError: (state, action) => {
      state.editError = action.payload;
    },
    setReorderIds: (state, action) => {
      state.reorderIds = action.payload.ids;
    }
  },
  extraReducers: (builder) => {
    builder
      // #index
      .addCase(fetchAssets.pending, (state, _action) => {
        state.status = 'loading'
      })
      .addCase(fetchAssets.fulfilled, (state, action) => {
        state.count = action.payload.count;
        state.entities = action.payload.assets;
        state.ids = action.payload.ids;
        state.status = 'succeeded';
      })
      .addCase(fetchAssets.rejected, (state, action) => {
        state.status = 'failed';
        state.error = action.error.message || 'Unknown Error';
      })

      // #create
      .addCase(createAsset.fulfilled, (state, action) => {
        const asset = Object.values(action.payload.asset)[0];
        state.entities[asset.id] = asset;
      })
      .addCase(createAsset.rejected, (state, action) => {
        const error = Array.isArray(action.payload) ? action.payload[0] : null;
        state.newError = error || action.error.message || 'Unknown Error';
      })

      // #show
      .addCase(fetchAsset.fulfilled, (state, action) => {
        const asset = Object.values(action.payload.asset)[0];
        state.entities[asset.id] = asset;
      })

      // #update
      .addCase(updateAsset.fulfilled, (state, action) => {
        const asset = Object.values(action.payload.asset)[0];
        state.entities[asset.id] = asset;
      })
      .addCase(updateAsset.rejected, (state, action) => {
        const error = Array.isArray(action.payload) ? action.payload[0] : null;
        state.editError = error || action.error.message || 'Unknown Error';
      })

      // #reorder
      .addCase(reorderAssets.fulfilled, (state, action) => {
        state.ids = action.payload.ids;
      })
  }
});

const reducer = slice.reducer;
export default reducer;

export const {
  setEditId,
  setNewParams,
  setNewGroupParams,
  setNewError,
  setEditError,
  setReorderIds
} = slice.actions;

export const {
  selectById: selectAssetById,
  selectIds: selectAssetIds,
  selectEntities: selectAssetEntities,
}  = adaptor.getSelectors<RootState>(state => state.mineCraft.assets);
